import type { SceneHost, SceneObject } from "../SceneHost";
import { applySceneObjectTransform } from "../sceneObjectDom";
import {
  applyPathGeometry,
  clonePoints,
  isPathObject,
  syncPathElement,
  translatePathPoints,
  type PathPoint,
} from "../pathGeometry";
import { applySegmentPoints } from "../bindings/resolveBindings";
import { hitTestTopObject, objectIntersectsSelectionRect } from "../HitTest";
import { isSegmentObject } from "../segmentGeometry";
import {
  isOverlayEditableObject,
  objectHref,
  openLinkHref,
  openObjectHref,
  pieceLinkAtPoint,
} from "../textObject";
import { isFrameObject, pointInFrameTitleBand } from "../frame/frameContext";
import { syncSegmentOnGroup } from "../render/renderers/segment";
import {
  sceneSvgDefs,
  sceneLayerSvgFor,
  syncSceneLayerSurface,
} from "../render/SvgRenderer";
import { objectBounds, unionBounds, type Bounds } from "../selectionBounds";
import {
  SmartGuideOverlay,
  shiftedBounds,
  snapBoundsToObjects,
  snapBoundsToGrid,
  snapThresholdWorld,
  SMART_GUIDE_SNAP_PX,
  isAlignSnapTarget,
  visibleWorldBoundsFromViewport,
} from "../smartGuides";
import { edgePanDelta, isNearViewportEdge } from "../edgeAutoPan";
import { collectPdfAnnotationIds } from "../document/documentContext";
import { isBoardEditorChrome } from "./boardWidgetChrome";

type DragOrigin = { x: number; y: number; w?: number; h?: number; points?: PathPoint[] };

type GroupDragState = {
  pointerId: number;
  pointerAnchor: { x: number; y: number };
  origins: Map<string, DragOrigin>;
  /** Аннотации PDF, которые едут вместе с презентацией (не в выделении). */
  pdfFollowers: Map<string, DragOrigin>;
  startBounds: Bounds;
  candidateBounds: Bounds[];
  guides: SmartGuideOverlay;
  moved: boolean;
  /** Повторный клик по уже выделенному тексту/стикеру — открыть редактор. */
  openTextEditOnClick: boolean;
  lastClientX: number;
  lastClientY: number;
  shiftKey: boolean;
  altKey: boolean;
};

type MarqueeState = {
  pointerId: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  shiftKey: boolean;
  el: HTMLElement;
  lastClientX: number;
  lastClientY: number;
};

/** Touch: не выделяем на pointerdown — ждём move/up или отмену 2-м пальцем. */
type PendingTouchSelect = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  lastClientX: number;
  lastClientY: number;
  shiftKey: boolean;
  pickedId: string | null;
  /** Выделение до начала жеста — восстановить при pan/pinch. */
  selectionSnapshot: string[];
  openTextEditOnClick: boolean;
  fromSelectionBox: boolean;
};

const MARQUEE_MIN_DRAG_PX = 4;
const MARQUEE_BORDER_SCREEN_PX = 1;
/**
 * Запас вокруг указателя при выборе объекта.
 *
 * Тонкую линию приходилось ловить почти попаданием в неё: восьми точек
 * хватает крупным объектам, а у волоска толщиной в пиксель это и есть вся
 * площадь. Четырнадцать — обычный размер цели под мышь, и объект под
 * указателем всё равно выбирается верхний, так что соседей это не отнимает.
 */
const PICK_RADIUS_MOUSE_PX = 14;
const PICK_RADIUS_TOUCH_PX = 28;
/** Пока палец сдвинулся меньше — ждём второй палец (pan), не коммитим выделение. */
const TOUCH_SELECT_COMMIT_PX = 12;

/** Сколько world-единиц приходится на 1 CSS-пиксель экрана (учитывает zoom камеры). */
function worldUnitsPerScreenPx(host: SceneHost): number {
  const a = host.stagePoint(0, 0);
  const b = host.stagePoint(1, 0);
  const world = Math.abs(b.x - a.x);
  return Number.isFinite(world) && world > 0 ? world : 1;
}

function pickRadiusWorld(host: SceneHost, ev: PointerEvent): number {
  const screenPx =
    ev.pointerType === "touch" || ev.pointerType === "pen"
      ? PICK_RADIUS_TOUCH_PX
      : PICK_RADIUS_MOUSE_PX;
  return screenPx * worldUnitsPerScreenPx(host);
}

function captureDragOrigin(obj: SceneObject): DragOrigin {
  if (isPathObject(obj)) {
    return {
      x: obj.x,
      y: obj.y,
      w: obj.w,
      h: obj.h,
      points: clonePoints(obj.points),
    };
  }
  if (isSegmentObject(obj)) {
    return {
      x: obj.x,
      y: obj.y,
      w: obj.w,
      h: obj.h,
      points: clonePoints(obj.points),
    };
  }
  return { x: obj.x, y: obj.y };
}

function collectDocumentFollowerOrigins(
  host: SceneHost,
  movedIds: Set<string>
): Map<string, DragOrigin> {
  const followers = new Map<string, DragOrigin>();
  for (const id of movedIds) {
    const obj = host.findObject(id);
    if (!obj || (obj.type !== "pdf_document" && obj.type !== "image")) continue;
    for (const annId of collectPdfAnnotationIds(host.getObjects(), id)) {
      if (movedIds.has(annId) || followers.has(annId)) continue;
      const ann = host.findObject(annId);
      if (!ann) continue;
      followers.set(annId, captureDragOrigin(ann));
    }
  }
  return followers;
}

function applyDragOriginAt(
  host: SceneHost,
  id: string,
  origin: DragOrigin,
  dx: number,
  dy: number
) {
  const obj = host.findObject(id);
  const el = host.getObjectElement(id);
  if (!obj || !el) return;
  if (isPathObject(obj) && origin.points) {
    const nextPoints = translatePathPoints(origin.points, dx, dy);
    applyPathGeometry(obj, nextPoints);
    syncPathElement(el, obj);
  } else if (isSegmentObject(obj) && origin.points && origin.points.length >= 2) {
    const nextPoints = translatePathPoints(origin.points, dx, dy);
    applySegmentPoints(obj, nextPoints[0]!, nextPoints[1]!);
    if (el instanceof SVGGElement) {
      syncSegmentOnGroup(el, obj, {
        defs: sceneSvgDefs(host.layer),
        markerEnd: obj.type === "arrow",
      });
    }
  } else {
    obj.x = Math.round(origin.x + dx);
    obj.y = Math.round(origin.y + dy);
    applySceneObjectTransform(el, obj);
  }
}

/**
 * Объект с вёрсткой рисуется в окне слоя и обрезается по его краю. При
 * перетаскивании поверхность обязана ехать следом, иначе презентация или
 * картинка уезжает за край и выглядит так, будто тонет под доской.
 */
function syncSurfaceAfterMove(host: SceneHost, id: string | undefined): void {
  if (!id) return;
  const svg = sceneLayerSvgFor(host.getObjectElement(id));
  if (svg) syncSceneLayerSurface(svg, host.getObjects());
}

function revertDragPositions(host: SceneHost, state: GroupDragState) {
  state.origins.forEach((origin, id) => applyDragOriginAt(host, id, origin, 0, 0));
  state.pdfFollowers.forEach((origin, id) => applyDragOriginAt(host, id, origin, 0, 0));
}

function normalizeRect(x0: number, y0: number, x1: number, y1: number) {
  const x = Math.min(x0, x1);
  const y = Math.min(y0, y1);
  return { x, y, w: Math.abs(x1 - x0), h: Math.abs(y1 - y0) };
}

function applyMarqueeEl(
  host: SceneHost,
  el: HTMLElement,
  x0: number,
  y0: number,
  x1: number,
  y1: number
) {
  const r = normalizeRect(x0, y0, x1, y1);
  // Геометрия — в world; обводка — ~1px экрана, иначе при zoom рамка «как объект».
  const borderWorld = MARQUEE_BORDER_SCREEN_PX * worldUnitsPerScreenPx(host);
  el.style.left = `${r.x}px`;
  el.style.top = `${r.y}px`;
  el.style.width = `${r.w}px`;
  el.style.height = `${r.h}px`;
  el.style.borderWidth = `${borderWorld}px`;
}

function constrainAxis(dx: number, dy: number): { dx: number; dy: number } {
  if (Math.abs(dx) >= Math.abs(dy)) return { dx, dy: 0 };
  return { dx: 0, dy };
}

function isCanvasPanGesture(ev: PointerEvent): boolean {
  // Пробела здесь больше нет: проверка была написана как
  // getModifierState("Space"), а пробел не модификатор — она всегда ложна,
  // и сдвиг по пробелу не работал ни разу. Холст двигают правой и средней
  // кнопкой, а пробел занят выделением и сменой пера (useSpaceGesture).
  return ev.button === 1 || ev.button === 2;
}

/** Виджеты, которые выделяются только кнопкой «Правка». */
function isEditGatedWidgetType(type: string | undefined): boolean {
  return type === "timer" || type === "calculator" || type === "code" || type === "sql";
}

function editGateSelector(type: string): string {
  if (type === "calculator") return ".wb-calc-card__edit";
  if (type === "code") return ".wb-code-card__edit";
  if (type === "sql") return ".wb-sql-card__edit";
  return ".wb-timer-card__edit";
}

const EDIT_GATE_BUTTON_SEL =
  ".wb-calc-card__edit, .wb-code-card__edit, .wb-sql-card__edit, .wb-timer-card__edit";

function isEditGateButton(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(EDIT_GATE_BUTTON_SEL));
}

/** id объекта: closest() из HTML внутри foreignObject часто не видит SVG `<g>`. */
function objectIdFromNode(start: EventTarget | null): string | null {
  let node: Node | null = start instanceof Node ? start : null;
  while (node) {
    if (node instanceof Element) {
      const id = node.getAttribute("data-object-id");
      if (id) return id;
    }
    node = node.parentNode;
  }
  return null;
}

/**
 * «Правка» под курсором — даже если сверху рамка выделения
 * (target = .wb-selection-frame__box, а кнопка ниже в стеке).
 */
function resolveEditGateObjectId(ev: PointerEvent): string | null {
  if (isEditGateButton(ev.target)) {
    const id = objectIdFromNode(ev.target);
    if (id) return id;
  }

  if (typeof document.elementsFromPoint !== "function") return null;
  const stack = document.elementsFromPoint(ev.clientX, ev.clientY);
  for (const el of stack) {
    if (!(el instanceof Element)) continue;
    if (el.closest(EDIT_GATE_BUTTON_SEL)) {
      const id = objectIdFromNode(el);
      if (id) return id;
    }
  }
  return null;
}

/** Клик внутри gated-виджета, но не по «Правка». */
function isEditGatedWidgetChrome(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (isEditGateButton(target)) return false;
  return Boolean(
    target.closest(
      ".wb-scene-obj--timer, .wb-timer-card, .wb-scene-obj--calculator, .wb-calc-card, .wb-scene-obj--code, .wb-code-card"
    )
  );
}

/** Кнопки/поля внутри виджета — жест select не перехватываем. */
function isWidgetInteractiveControl(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(WIDGET_CONTROL_SEL));
}

/**
 * «Начинка» виджетов, которая сама обрабатывает указатель: плеер, кнопки и поля,
 * редактор кода (CodeMirror), холст графика и его разделитель, навигация PDF.
 * По ней объект не таскаем — событие уходит самому виджету.
 */
const WIDGET_CONTROL_SEL = [
  "button",
  "input",
  "textarea",
  "select",
  "a",
  "audio",
  ".wb-audio-card__player",
  ".wb-audio-card__together-label",
  ".wb-pdf-nav",
  ".wb-pdf-nav__btn",
  ".wb-pdf-thumbs",
  ".wb-pdf-thumbs__btn",
  ".wb-code-card__editor-host",
  ".wb-calc-card__plot-wrap",
  ".wb-calc-card__graph-split",
].join(", ");

/** Карточки виджетов, у которых есть собственные контролы. */
const WIDGET_CARD_SEL =
  ".wb-audio-card, .wb-timer-card, .wb-calc-card, .wb-code-card";

/**
 * Тело виджет-карточки (заголовок, отступы, пустые места) — обычный объект,
 * его надо таскать. Карточки входят в WIDGET_CHROME_SELECTOR, из-за чего
 * select-инструмент отбрасывал по ним любой pointerdown: невыделенное
 * аудио/голосовое становилось неперетаскиваемым.
 */
function isWidgetCardDragBody(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (!target.closest(WIDGET_CARD_SEL)) return false;
  return !target.closest(WIDGET_CONTROL_SEL);
}

function isTransformHandle(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    Boolean(
      target.closest(
        ".wb-selection-handle, .wb-segment-point-handle, .wb-connector-anchor, .wb-pdf-nav, .wb-pdf-nav__btn, .wb-pdf-thumbs, .wb-pdf-thumbs__btn, .wb-audio-card__player, .wb-audio-card__together-label, audio"
      )
    )
  );
}

export type SelectToolBinding = {
  destroy: () => void;
};

export function bindSelectTool(host: SceneHost): SelectToolBinding {
  let drag: GroupDragState | null = null;
  let marquee: MarqueeState | null = null;
  let pendingTouch: PendingTouchSelect | null = null;
  let pointerSession = false;
  let chromeRaf = 0;
  let edgePanRaf = 0;
  let edgePanLastTs = 0;
  let didEdgePan = false;

  function viewportEl(): Element {
    return (
      host.selectionOverlay.closest(".wb-board-viewport") ??
      host.stage.closest(".wb-board-viewport") ??
      host.hitSurface
    );
  }

  function stopEdgePan() {
    if (edgePanRaf) {
      cancelAnimationFrame(edgePanRaf);
      edgePanRaf = 0;
    }
    edgePanLastTs = 0;
  }

  function finishEdgePanSession() {
    const used = didEdgePan;
    stopEdgePan();
    didEdgePan = false;
    if (used) {
      host.endPanGesture?.();
    }
  }

  function tickEdgePan(now: number) {
    edgePanRaf = 0;
    if (!drag && !marquee) {
      edgePanLastTs = 0;
      return;
    }
    const clientX = drag?.lastClientX ?? marquee!.lastClientX;
    const clientY = drag?.lastClientY ?? marquee!.lastClientY;
    const rect = viewportEl().getBoundingClientRect();
    if (!isNearViewportEdge(clientX, clientY, rect)) {
      edgePanLastTs = 0;
      return;
    }
    const dt = edgePanLastTs ? Math.min(34, Math.max(0, now - edgePanLastTs)) : 16;
    edgePanLastTs = now;
    const sample = edgePanDelta(clientX, clientY, rect, dt);
    if (!sample.active) {
      edgePanLastTs = 0;
      return;
    }
    didEdgePan = true;
    host.panBy(sample.dx, sample.dy);
    if (drag) {
      applyGroupDragAt(drag.lastClientX, drag.lastClientY, drag.shiftKey, drag.altKey);
    } else if (marquee) {
      applyMarqueeAt(marquee.lastClientX, marquee.lastClientY);
    }
    edgePanRaf = requestAnimationFrame(tickEdgePan);
  }

  function ensureEdgePan() {
    if (edgePanRaf) return;
    if (!drag && !marquee) return;
    const clientX = drag?.lastClientX ?? marquee!.lastClientX;
    const clientY = drag?.lastClientY ?? marquee!.lastClientY;
    const rect = viewportEl().getBoundingClientRect();
    if (!isNearViewportEdge(clientX, clientY, rect)) return;
    edgePanRaf = requestAnimationFrame(tickEdgePan);
  }

  function setSelectionDragging(on: boolean) {
    const vp = viewportEl();
    vp.classList.toggle("is-selection-dragging", on);
    window.dispatchEvent(new CustomEvent("wb:selection-drag", { detail: on }));
  }

  function scheduleSelectionChrome() {
    if (chromeRaf) return;
    chromeRaf = requestAnimationFrame(() => {
      chromeRaf = 0;
      host.refreshSelectionChrome();
    });
  }

  function flushSelectionChrome() {
    if (chromeRaf) {
      cancelAnimationFrame(chromeRaf);
      chromeRaf = 0;
    }
    host.refreshSelectionChrome();
  }

  function attachPointerSession() {
    if (pointerSession) return;
    pointerSession = true;
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerCancel);
  }

  function detachPointerSession() {
    if (!pointerSession) return;
    pointerSession = false;
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("pointercancel", onPointerCancel);
  }

  function releasePointerCapture(ev?: PointerEvent) {
    if (!ev) return;
    if (host.hitSurface.hasPointerCapture(ev.pointerId)) {
      host.hitSurface.releasePointerCapture(ev.pointerId);
    }
  }

  function resolvePickedId(ev: PointerEvent): string | null {
    // Клик по виджету (кроме «Правка») не должен выделять через hit-test.
    if (isEditGatedWidgetChrome(ev.target)) {
      return null;
    }

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    const hit = hitTestTopObject(host.getObjects(), pt.x, pt.y, pickRadiusWorld(host, ev));
    if (hit) {
      if (isEditGatedWidgetType(hit.type)) {
        const editSel = editGateSelector(hit.type);
        if (!(ev.target instanceof Element) || !ev.target.closest(editSel)) {
          return null;
        }
      }
      return hit.id;
    }

    if (ev.target instanceof Element) {
      const t = ev.target.closest(".wb-scene-obj");
      if (t && host.layer.contains(t)) {
        const id = t.getAttribute("data-object-id");
        if (t.classList.contains("wb-scene-obj--calculator")) {
          if (!ev.target.closest(".wb-calc-card__edit")) return null;
        }
        if (t.classList.contains("wb-scene-obj--code")) {
          if (!ev.target.closest(".wb-code-card__edit")) return null;
        }
        if (t.classList.contains("wb-scene-obj--timer")) {
          if (!ev.target.closest(".wb-timer-card__edit")) return null;
        }
        return id;
      }
    }
    return null;
  }

  function draggableSelectionIds(): string[] {
    const ids = host.getSelection();
    const objs = ids
      .map((sid) => host.findObject(sid))
      .filter((o): o is NonNullable<typeof o> => Boolean(o));
    // Если в выделении есть хотя бы один незаблокированный — двигаем всю группу
    // (в т.ч. locked линии/подписи виджетов), иначе locked-only не таскаем.
    if (!objs.some((o) => !o.locked)) return [];
    return ids.filter((sid) => Boolean(host.findObject(sid)));
  }

  function cancelActiveInteraction() {
    if (drag) endDrag(false);
    if (marquee) endMarquee(false);
    pendingTouch = null;
  }

  function onViewportGestureStart() {
    // 2 пальца: откатываем выделение, если успели его сменить (или pending ещё висит).
    const snap = pendingTouch?.selectionSnapshot;
    cancelActiveInteraction();
    if (snap) {
      host.setSelection(snap);
      flushSelectionChrome();
    }
    finishEdgePanSession();
    detachPointerSession();
  }

  function commitPendingTouchAsInteraction(ev: PointerEvent, asClick: boolean) {
    if (!pendingTouch || pendingTouch.pointerId !== ev.pointerId) return;
    const pending = pendingTouch;
    pendingTouch = null;

    if (asClick) {
      if (pending.shiftKey && pending.pickedId) {
        const cur = new Set(pending.selectionSnapshot);
        if (cur.has(pending.pickedId)) cur.delete(pending.pickedId);
        else cur.add(pending.pickedId);
        host.setSelection([...cur]);
        flushSelectionChrome();
      } else if (pending.pickedId) {
        host.setSelection([pending.pickedId]);
        flushSelectionChrome();
        if (pressedLinkHref) {
          const href = pressedLinkHref;
          pressedLinkHref = null;
          openLinkHref(href);
        } else if (pending.openTextEditOnClick) {
          host.requestTextEdit?.(pending.pickedId);
        }
      } else {
        // Тап по пустому — снять выделение
        host.setSelection([]);
        flushSelectionChrome();
      }
      detachPointerSession();
      releasePointerCapture(ev);
      return;
    }

    // Драг: выделяем и тащим / рисуем marquee
    if (pending.fromSelectionBox) {
      const ids = draggableSelectionIds();
      if (ids.length) {
        startGroupDrag(ev, ids, pending.openTextEditOnClick);
      } else {
        detachPointerSession();
        releasePointerCapture(ev);
      }
      return;
    }
    if (pending.pickedId) {
      if (!host.getSelection().includes(pending.pickedId)) {
        host.setSelection([pending.pickedId]);
        flushSelectionChrome();
      }
      const ids = draggableSelectionIds();
      if (ids.length) {
        startGroupDrag(ev, ids, pending.openTextEditOnClick);
      } else {
        detachPointerSession();
        releasePointerCapture(ev);
      }
      return;
    }
    const startWorld = host.stagePoint(pending.startClientX, pending.startClientY);
    startMarquee(ev);
    if (marquee) {
      marquee.x0 = startWorld.x;
      marquee.y0 = startWorld.y;
      applyMarqueeAt(ev.clientX, ev.clientY);
    }
  }

  function startMarquee(ev: PointerEvent) {
    cancelActiveInteraction();

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    const box = document.createElement("div");
    box.className = "wb-selection-marquee";
    host.selectionOverlay.appendChild(box);
    applyMarqueeEl(host, box, pt.x, pt.y, pt.x, pt.y);

    marquee = {
      pointerId: ev.pointerId,
      x0: pt.x,
      y0: pt.y,
      x1: pt.x,
      y1: pt.y,
      shiftKey: ev.shiftKey,
      el: box,
      lastClientX: ev.clientX,
      lastClientY: ev.clientY,
    };

    if (host.hitSurface.setPointerCapture) {
      host.hitSurface.setPointerCapture(ev.pointerId);
    }
    attachPointerSession();
  }

  function applyMarqueeAt(clientX: number, clientY: number) {
    if (!marquee) return;
    const pt = host.stagePoint(clientX, clientY);
    marquee.x1 = pt.x;
    marquee.y1 = pt.y;
    applyMarqueeEl(host, marquee.el, marquee.x0, marquee.y0, marquee.x1, marquee.y1);
  }

  function endMarquee(apply: boolean, ev?: PointerEvent) {
    if (!marquee) return;
    const state = marquee;
    marquee = null;
    finishEdgePanSession();
    detachPointerSession();
    releasePointerCapture(ev);
    state.el.remove();

    if (!apply) return;

    const r = normalizeRect(state.x0, state.y0, state.x1, state.y1);
    const minWorld = MARQUEE_MIN_DRAG_PX * worldUnitsPerScreenPx(host);
    const isClick = r.w < minWorld && r.h < minWorld;

    if (isClick) {
      if (!state.shiftKey) host.setSelection([]);
      flushSelectionChrome();
      return;
    }

    const hits = host
      .getObjects()
      .filter((obj) => !isEditGatedWidgetType(obj.type) && objectIntersectsSelectionRect(obj, r))
      .map((obj) => obj.id);

    if (state.shiftKey) {
      const merged = new Set(host.getSelection());
      hits.forEach((id) => merged.add(id));
      host.setSelection([...merged]);
    } else {
      host.setSelection(hits);
    }
    flushSelectionChrome();
  }

  function startGroupDrag(ev: PointerEvent, ids: string[], openTextEditOnClick = false) {
    cancelActiveInteraction();

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    const origins = new Map<string, DragOrigin>();
    for (const id of ids) {
      const obj = host.findObject(id);
      if (!obj) continue;
      origins.set(id, captureDragOrigin(obj));
    }
    const startBounds = unionBounds(ids.map((id) => host.findObject(id)).filter(Boolean) as SceneObject[]);
    if (!startBounds) return;
    const idSet = new Set(ids);
    const pdfFollowers = collectDocumentFollowerOrigins(host, idSet);
    const candidateBounds = host
      .getObjects()
      .filter((obj) => !idSet.has(obj.id) && isAlignSnapTarget(obj))
      .map((obj) => objectBounds(obj));

    const guides = new SmartGuideOverlay(host.selectionOverlay);

    drag = {
      pointerId: ev.pointerId,
      pointerAnchor: pt,
      origins,
      pdfFollowers,
      startBounds,
      candidateBounds,
      guides,
      moved: false,
      openTextEditOnClick,
      lastClientX: ev.clientX,
      lastClientY: ev.clientY,
      shiftKey: ev.shiftKey,
      altKey: ev.altKey,
    };

    for (const id of ids) {
      host.getObjectElement(id)?.classList.add("is-dragging");
    }
    for (const id of pdfFollowers.keys()) {
      host.getObjectElement(id)?.classList.add("is-dragging");
    }
    setSelectionDragging(true);

    if (host.hitSurface.setPointerCapture) {
      host.hitSurface.setPointerCapture(ev.pointerId);
    }
    attachPointerSession();
  }

  /**
   * Точка внутри рамки выделения, и под ней нет чужого объекта.
   * Нужно для «пустых» объектов вроде нарисованной буквы С: у неё
   * pointer-events только по штриху, но тащить логично из любой точки рамки.
   */
  function pointInsideSelectionBox(ev: PointerEvent, ids: string[]): boolean {
    const objects = ids
      .map((id) => host.findObject(id))
      .filter(Boolean) as SceneObject[];
    if (!objects.length) return false;
    const b = unionBounds(objects);
    if (!b || b.w < 1 || b.h < 1) return false;

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    if (pt.x < b.x || pt.x > b.x + b.w || pt.y < b.y || pt.y > b.y + b.h) return false;

    // Клик по другому объекту внутри рамки — выделяем его, а не тащим текущее.
    const top = hitTestTopObject(host.getObjects(), pt.x, pt.y, pickRadiusWorld(host, ev));
    if (top && !ids.includes(top.id)) return false;
    return true;
  }

  function beginDragFromSelection(ev: PointerEvent): boolean {
    const ids = draggableSelectionIds();
    if (!ids.length) return false;

    // Тащим за синюю рамку выделения: по самой рамке или по любой точке внутри неё.
    const onFrameBox =
      ev.target instanceof Element && Boolean(ev.target.closest(".wb-selection-frame__box"));
    if (!onFrameBox && !pointInsideSelectionBox(ev, ids)) {
      return false;
    }

    const soleId = ids.length === 1 ? ids[0]! : null;
    const soleObj = soleId ? host.findObject(soleId) : null;
    const openTextEditOnClick = Boolean(
      soleObj && isOverlayEditableObject(soleObj) && !objectHref(soleObj)
    );

    ev.preventDefault();
    ev.stopPropagation();
    if (ev.pointerType === "touch") {
      pendingTouch = {
        pointerId: ev.pointerId,
        startClientX: ev.clientX,
        startClientY: ev.clientY,
        lastClientX: ev.clientX,
        lastClientY: ev.clientY,
        shiftKey: ev.shiftKey,
        pickedId: soleId,
        selectionSnapshot: host.getSelection().slice(),
        openTextEditOnClick,
        fromSelectionBox: true,
      };
      if (host.hitSurface.setPointerCapture) {
        host.hitSurface.setPointerCapture(ev.pointerId);
      }
      attachPointerSession();
      return true;
    }
    startGroupDrag(ev, ids, openTextEditOnClick);
    return true;
  }

  /**
   * Ссылка на куске надписи, по которой нажали. Щелчок без перетаскивания
   * открывает её, как в Miro; перетаскивание — просто двигает надпись.
   */
  let pressedLinkHref: string | null = null;

  function tryOpenPickedHref(ev: PointerEvent, pickedId: string | null): boolean {
    if (!pickedId) return false;
    const obj = host.findObject(pickedId);
    if (!objectHref(obj)) return false;
    // Ctrl/Cmd+клик — сразу открыть (не начинать drag)
    if (!(ev.ctrlKey || ev.metaKey)) return false;
    ev.preventDefault();
    ev.stopPropagation();
    openObjectHref(obj);
    return true;
  }

  function onSelectPointerDown(ev: PointerEvent) {
    if (host.getActiveTool() !== "select") return;
    // Ссылка прошлого нажатия не должна открыться при этом.
    pressedLinkHref = null;
    if (host.isInteractionBlocked() || isTransformHandle(ev.target)) return;
    if (ev.button !== 0 || isCanvasPanGesture(ev)) return;
    if (isBoardEditorChrome(ev.target) && !isWidgetCardDragBody(ev.target)) return;

    // «Правка»: выделить и не начинать drag.
    // id ищем через parentNode / elementsFromPoint — foreignObject + рамка выделения
    // ломают обычный closest(".wb-scene-obj").
    if (host.canEdit) {
      const editId = resolveEditGateObjectId(ev);
      if (editId) {
        host.setSelection([editId]);
        flushSelectionChrome();
        return;
      }
    }

    const onSelectionBox =
      ev.target instanceof Element &&
      Boolean(ev.target.closest(".wb-selection-frame__box"));

    // Уже выделенный таймер/калькулятор/код — тащим за синюю рамку или тело карточки
    // (но не перехватываем Старт/Пауза/поля ввода/«Правка»).
    if (host.canEdit) {
      const ptGate = host.stagePoint(ev.clientX, ev.clientY);
      let topGate = hitTestTopObject(
        host.getObjects(),
        ptGate.x,
        ptGate.y,
        pickRadiusWorld(host, ev)
      );
      // При наложении: если курсор на DOM выделенного виджета — тащим его
      // (hit-test по world мог вернуть нижний из‑за порядка в массиве).
      const domId = objectIdFromNode(ev.target);
      if (domId && host.getSelection().includes(domId)) {
        const domObj = host.findObject(domId);
        if (domObj && isEditGatedWidgetType(domObj.type)) {
          topGate = domObj;
        }
      }
      if (
        topGate &&
        isEditGatedWidgetType(topGate.type) &&
        host.getSelection().includes(topGate.id)
      ) {
        if (isWidgetInteractiveControl(ev.target) && !onSelectionBox) {
          return;
        }
        const draggableIds = draggableSelectionIds();
        if (draggableIds.length) {
          ev.preventDefault();
          ev.stopPropagation();
          if (ev.pointerType === "touch") {
            pendingTouch = {
              pointerId: ev.pointerId,
              startClientX: ev.clientX,
              startClientY: ev.clientY,
              lastClientX: ev.clientX,
              lastClientY: ev.clientY,
              shiftKey: ev.shiftKey,
              pickedId: null,
              selectionSnapshot: host.getSelection().slice(),
              openTextEditOnClick: false,
              fromSelectionBox: true,
            };
            if (host.hitSurface.setPointerCapture) {
              host.hitSurface.setPointerCapture(ev.pointerId);
            }
            attachPointerSession();
            return;
          }
          startGroupDrag(ev, draggableIds);
          return;
        }
      }
    }

    // Невыделенный gated-виджет: без «Правка» не выделяем и не начинаем marquee.
    if (isEditGatedWidgetChrome(ev.target)) return;
    {
      const pt = host.stagePoint(ev.clientX, ev.clientY);
      const top = hitTestTopObject(host.getObjects(), pt.x, pt.y, pickRadiusWorld(host, ev));
      if (
        top &&
        isEditGatedWidgetType(top.type) &&
        (!(ev.target instanceof Element) ||
          !ev.target.closest(editGateSelector(top.type)))
      ) {
        return;
      }
    }

    // Старый путь: calc/code целиком в transform-handle больше не нужен —
    // клики по их карточкам закрыты edit-gate выше. Но calc/code без выделения
    // всё ещё не должны начинать marquee через isTransformHandle-подобный щит.
    if (
      ev.target instanceof Element &&
      ev.target.closest(".wb-calc-card, .wb-code-card, .wb-timer-card") &&
      !ev.target.closest(".wb-calc-card__edit, .wb-code-card__edit, .wb-timer-card__edit")
    ) {
      return;
    }

    const pickedId = resolvePickedId(ev);
    pressedLinkHref = pickedId
      ? pieceLinkAtPoint(host.getObjectElement(pickedId), ev.clientX, ev.clientY)
      : null;
    // Ctrl+щелчок и доска «только смотреть» — открываем сразу.
    if (pressedLinkHref && (ev.ctrlKey || ev.metaKey || !host.canEdit)) {
      ev.preventDefault();
      ev.stopPropagation();
      const href = pressedLinkHref;
      pressedLinkHref = null;
      openLinkHref(href);
      return;
    }
    if (tryOpenPickedHref(ev, pickedId)) return;

    if (!host.canEdit) {
      // view-only: обычный клик по ссылке
      if (pickedId) {
        const obj = host.findObject(pickedId);
        if (objectHref(obj)) {
          ev.preventDefault();
          ev.stopPropagation();
          openObjectHref(obj);
        }
      }
      return;
    }

    if (pickedId && ev.shiftKey) {
      ev.preventDefault();
      ev.stopPropagation();
      // Touch+shift редко; на таче отложим до up
      if (ev.pointerType === "touch") {
        pendingTouch = {
          pointerId: ev.pointerId,
          startClientX: ev.clientX,
          startClientY: ev.clientY,
          lastClientX: ev.clientX,
          lastClientY: ev.clientY,
          shiftKey: true,
          pickedId,
          selectionSnapshot: host.getSelection().slice(),
          openTextEditOnClick: false,
          fromSelectionBox: false,
        };
        if (host.hitSurface.setPointerCapture) {
          host.hitSurface.setPointerCapture(ev.pointerId);
        }
        attachPointerSession();
        return;
      }
      const cur = new Set(host.getSelection());
      if (cur.has(pickedId)) {
        cur.delete(pickedId);
      } else {
        cur.add(pickedId);
      }
      host.setSelection([...cur]);
      flushSelectionChrome();
      return;
    }

    if (beginDragFromSelection(ev)) return;

    if (pickedId) {
      ev.preventDefault();
      ev.stopPropagation();

      const wasAlreadySelected =
        host.getSelection().length === 1 && host.getSelection()[0] === pickedId;
      const pickedObj = host.findObject(pickedId);
      const hasHref = Boolean(objectHref(pickedObj));
      const openTextEditOnClick =
        wasAlreadySelected &&
        Boolean(pickedObj && isOverlayEditableObject(pickedObj) && !hasHref);

      // Touch: не выделяем до move/up — иначе 2-й палец «залипает» выделение.
      if (ev.pointerType === "touch") {
        pendingTouch = {
          pointerId: ev.pointerId,
          startClientX: ev.clientX,
          startClientY: ev.clientY,
          lastClientX: ev.clientX,
          lastClientY: ev.clientY,
          shiftKey: ev.shiftKey,
          pickedId,
          selectionSnapshot: host.getSelection().slice(),
          openTextEditOnClick,
          fromSelectionBox: false,
        };
        if (host.hitSurface.setPointerCapture) {
          host.hitSurface.setPointerCapture(ev.pointerId);
        }
        attachPointerSession();
        return;
      }

      if (!host.getSelection().includes(pickedId)) {
        host.setSelection([pickedId]);
        flushSelectionChrome();
      }

      const draggableIds = draggableSelectionIds();
      if (!draggableIds.length) return;
      startGroupDrag(ev, draggableIds, openTextEditOnClick);
      return;
    }

    ev.preventDefault();
    if (ev.pointerType === "touch") {
      pendingTouch = {
        pointerId: ev.pointerId,
        startClientX: ev.clientX,
        startClientY: ev.clientY,
        lastClientX: ev.clientX,
        lastClientY: ev.clientY,
        shiftKey: ev.shiftKey,
        pickedId: null,
        selectionSnapshot: host.getSelection().slice(),
        openTextEditOnClick: false,
        fromSelectionBox: false,
      };
      if (host.hitSurface.setPointerCapture) {
        host.hitSurface.setPointerCapture(ev.pointerId);
      }
      attachPointerSession();
      return;
    }
    startMarquee(ev);
  }

  function onPointerMove(ev: PointerEvent) {
    if (pendingTouch && ev.pointerId === pendingTouch.pointerId) {
      pendingTouch.lastClientX = ev.clientX;
      pendingTouch.lastClientY = ev.clientY;
      pendingTouch.shiftKey = ev.shiftKey;
      const dx = ev.clientX - pendingTouch.startClientX;
      const dy = ev.clientY - pendingTouch.startClientY;
      if (Math.hypot(dx, dy) >= TOUCH_SELECT_COMMIT_PX) {
        commitPendingTouchAsInteraction(ev, false);
      }
      return;
    }

    if (marquee && ev.pointerId === marquee.pointerId) {
      marquee.lastClientX = ev.clientX;
      marquee.lastClientY = ev.clientY;
      marquee.shiftKey = ev.shiftKey;
      applyMarqueeAt(ev.clientX, ev.clientY);
      ensureEdgePan();
      return;
    }

    if (!drag || ev.pointerId !== drag.pointerId) return;

    drag.lastClientX = ev.clientX;
    drag.lastClientY = ev.clientY;
    drag.shiftKey = ev.shiftKey;
    drag.altKey = ev.altKey;
    applyGroupDragAt(ev.clientX, ev.clientY, ev.shiftKey, ev.altKey);
    ensureEdgePan();
  }

  function applyGroupDragAt(
    clientX: number,
    clientY: number,
    shiftKey: boolean,
    altKey: boolean
  ) {
    if (!drag) return;

    const pt = host.stagePoint(clientX, clientY);
    let rawDx = pt.x - drag.pointerAnchor.x;
    let rawDy = pt.y - drag.pointerAnchor.y;
    if (shiftKey) {
      const constrained = constrainAxis(rawDx, rawDy);
      rawDx = constrained.dx;
      rawDy = constrained.dy;
    }
    if (Math.abs(rawDx) > 0 || Math.abs(rawDy) > 0) drag.moved = true;

    let dx = rawDx;
    let dy = rawDy;
    const movedBounds = shiftedBounds(drag.startBounds, rawDx, rawDy);

    // Alt — прилипание к сетке. Без Alt — магнит к краям/центрам соседних объектов.
    if (altKey) {
      const gridSnap = snapBoundsToGrid(movedBounds);
      dx += gridSnap.dx;
      dy += gridSnap.dy;
      drag.guides.renderAlignment();
      drag.guides.renderSpacing([]);
    } else if (drag.moved) {
      const map = (cx: number, cy: number) => host.stagePoint(cx, cy);
      const view = visibleWorldBoundsFromViewport(map, viewportEl());
      const snap = snapBoundsToObjects(
        movedBounds,
        drag.candidateBounds,
        snapThresholdWorld(map, SMART_GUIDE_SNAP_PX),
        view
      );
      dx += snap.dx;
      dy += snap.dy;
      drag.guides.renderAlignment(snap.guidesX, snap.guidesY);
      drag.guides.renderSpacing([]);
    }

    drag.origins.forEach((origin, id) => applyDragOriginAt(host, id, origin, dx, dy));
    drag.pdfFollowers.forEach((origin, id) => applyDragOriginAt(host, id, origin, dx, dy));
    syncSurfaceAfterMove(host, drag.origins.keys().next().value);
    scheduleSelectionChrome();
  }

  function endDrag(commit: boolean, ev?: PointerEvent) {
    if (!drag) return;
    const state = drag;
    drag = null;
    finishEdgePanSession();
    detachPointerSession();
    releasePointerCapture(ev);

    state.origins.forEach((_o, id) => {
      host.getObjectElement(id)?.classList.remove("is-dragging");
    });
    state.pdfFollowers.forEach((_o, id) => {
      host.getObjectElement(id)?.classList.remove("is-dragging");
    });
    setSelectionDragging(false);
    state.guides.destroy();

    if (!commit || !state.moved) {
      if (!commit && state.moved) revertDragPositions(host, state);
      if (commit && !state.moved && state.origins.size === 1) {
        const id = state.origins.keys().next().value;
        if (id) {
          const obj = host.findObject(id);
          if (pressedLinkHref) {
            const href = pressedLinkHref;
            pressedLinkHref = null;
            openLinkHref(href);
            flushSelectionChrome();
            return;
          }
          if (obj && objectHref(obj)) {
            openObjectHref(obj);
            flushSelectionChrome();
            return;
          }
          if (state.openTextEditOnClick && obj && isOverlayEditableObject(obj)) {
            host.requestTextEdit?.(id);
            return;
          }
        }
      }
      flushSelectionChrome();
      return;
    }

    const patches: { id: string; patch: Record<string, unknown> }[] = [];
    const originById = new Map<string, DragOrigin>([
      ...state.origins.entries(),
      ...state.pdfFollowers.entries(),
    ]);

    const pushMovePatch = (id: string) => {
      const obj = host.findObject(id);
      if (!obj) return;
      if (isPathObject(obj)) {
        patches.push({
          id,
          patch: { x: obj.x, y: obj.y, w: obj.w, h: obj.h, points: obj.points },
        });
      } else if (isSegmentObject(obj)) {
        patches.push({
          id,
          patch: { x: obj.x, y: obj.y, w: obj.w, h: obj.h, points: obj.points },
        });
      } else {
        patches.push({ id, patch: { x: obj.x, y: obj.y } });
      }
    };

    state.origins.forEach((_origin, id) => pushMovePatch(id));
    state.pdfFollowers.forEach((_origin, id) => pushMovePatch(id));

    if (!patches.length) {
      flushSelectionChrome();
      return;
    }

    const forward = patches.map((p) => ({
      type: "update_object" as const,
      id: p.id,
      patch: p.patch,
    }));
    const inverse = patches
      .map((p) => {
        const origin = originById.get(p.id);
        if (!origin) return null;
        const invPatch: Record<string, unknown> = { x: origin.x, y: origin.y };
        if (origin.points) {
          invPatch.w = origin.w;
          invPatch.h = origin.h;
          invPatch.points = clonePoints(origin.points);
        }
        return { type: "update_object" as const, id: p.id, patch: invPatch };
      })
      .filter(Boolean) as {
      type: "update_object";
      id: string;
      patch: Record<string, unknown>;
    }[];

    void host
      .commitBatchPatches(patches, `move-${Date.now()}`, {
        forward,
        inverse: inverse.reverse(),
      })
      .catch((err) => {
        if (typeof console !== "undefined" && console.error) {
          console.error("[selectTool] move commit failed", err);
        }
      })
      .finally(() => flushSelectionChrome());
  }

  function onPointerUp(ev: PointerEvent) {
    if (pendingTouch && ev.pointerId === pendingTouch.pointerId) {
      commitPendingTouchAsInteraction(ev, true);
      return;
    }
    if (marquee && ev.pointerId === marquee.pointerId) {
      endMarquee(true, ev);
      return;
    }
    if (drag && ev.pointerId === drag.pointerId) {
      endDrag(true, ev);
    }
  }

  function onPointerCancel(ev: PointerEvent) {
    if (pendingTouch && ev.pointerId === pendingTouch.pointerId) {
      const snap = pendingTouch.selectionSnapshot;
      pendingTouch = null;
      host.setSelection(snap);
      flushSelectionChrome();
      detachPointerSession();
      return;
    }
    if (marquee && ev.pointerId === marquee.pointerId) {
      endMarquee(false, ev);
      return;
    }
    if (drag && ev.pointerId === drag.pointerId) {
      endDrag(false, ev);
    }
  }

  function onTextEditDblClick(ev: MouseEvent) {
    if (!host.canEdit || host.getActiveTool() !== "select") return;
    if (host.isInteractionBlocked()) return;

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    // Заголовок рамки — поверх всего: dblclick по подписи всегда переименовывает рамку.
    const objects = host.getObjects();
    for (let i = objects.length - 1; i >= 0; i--) {
      const candidate = objects[i]!;
      if (!isFrameObject(candidate) || candidate.locked) continue;
      if (!pointInFrameTitleBand(candidate, pt.x, pt.y)) continue;
      ev.preventDefault();
      ev.stopPropagation();
      host.setSelection([candidate.id]);
      flushSelectionChrome();
      host.requestTextEdit?.(candidate.id);
      return;
    }

    const id = resolvePickedId(ev as PointerEvent);
    if (!id) return;

    const obj = host.findObject(id);
    if (!obj || !isOverlayEditableObject(obj)) return;

    ev.preventDefault();
    ev.stopPropagation();
    host.setSelection([id]);
    flushSelectionChrome();
    host.requestTextEdit?.(id);
  }

  const pointerOpts: AddEventListenerOptions = { capture: true };

  host.hitSurface.addEventListener("pointerdown", onSelectPointerDown, pointerOpts);
  host.hitSurface.addEventListener("dblclick", onTextEditDblClick);
  window.addEventListener("wb:viewport-gesture-start", onViewportGestureStart);

  return {
    destroy() {
      if (chromeRaf) {
        cancelAnimationFrame(chromeRaf);
        chromeRaf = 0;
      }
      stopEdgePan();
      setSelectionDragging(false);
      cancelActiveInteraction();
      detachPointerSession();
      host.hitSurface.removeEventListener("pointerdown", onSelectPointerDown, pointerOpts);
      host.hitSurface.removeEventListener("dblclick", onTextEditDblClick);
      window.removeEventListener("wb:viewport-gesture-start", onViewportGestureStart);
    },
  };
}
